import * as DropdownPrimitive from '@radix-ui/react-dropdown-menu';
import { useState } from 'react';
import { Check, ChevronDown, Settings } from 'lucide-react';
import { micLabel } from '../../lib/local';
import { listMicrophones, preferredMicrophone } from '../../lib/voice';
import { ui } from '../../store/ui';
import { chooseMicrophone } from '../settings/UserSettings';

const item =
  'flex cursor-pointer select-none items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm text-fg outline-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-fg data-[disabled]:pointer-events-none data-[disabled]:opacity-60';

/** The arrow beside the microphone button: a quick list of microphones. */
export function MicMenu() {
  const [mics, setMics] = useState<MediaDeviceInfo[] | null>(null);
  const [inUse, setInUse] = useState<string | undefined>();
  const [chosenMissing, setChosenMissing] = useState(false);

  async function load() {
    try {
      const list = await listMicrophones();
      const preferred = await preferredMicrophone();
      setMics(list);
      setInUse(preferred);
      setChosenMissing(!preferred && !!micLabel.get() && list.some((d) => d.label));
    } catch {
      setMics([]);
    }
  }

  async function allowAccess() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
    } catch {
      ui.toast('The browser blocked the microphone. Allow it in the site settings to choose one.', 'error');
    }
    await load();
  }

  const named = mics?.some((d) => d.label);
  return (
    <DropdownPrimitive.Root modal={false} onOpenChange={(open) => open && void load()}>
      <DropdownPrimitive.Trigger asChild>
        <button
          type="button"
          aria-label="Choose microphone"
          title="Choose microphone"
          className="-ml-1 inline-flex h-8 w-4 shrink-0 items-center justify-center rounded-md text-muted transition-colors hover:bg-hover hover:text-fg data-[state=open]:bg-active data-[state=open]:text-fg"
        >
          <ChevronDown className="size-3.5" aria-hidden />
        </button>
      </DropdownPrimitive.Trigger>
      <DropdownPrimitive.Portal>
        <DropdownPrimitive.Content className="animate-pop z-[70] w-72 rounded-lg border border-line bg-overlay p-1.5 shadow-pop" side="top" align="start" sideOffset={6} collisionPadding={8}>
          <DropdownPrimitive.Label className="px-2.5 pt-1 pb-1.5 text-xs font-semibold uppercase tracking-wide text-faint">Input device</DropdownPrimitive.Label>
          {mics === null ? (
            <div className="px-2.5 py-1.5 text-sm text-muted">Looking for microphones…</div>
          ) : mics.length === 0 ? (
            <div className="px-2.5 py-1.5 text-sm text-muted">No microphone found. Plug one in and open this menu again.</div>
          ) : !named ? (
            <DropdownPrimitive.Item
              className={item}
              onSelect={(e) => {
                // Keep the menu open so the names appear once access is allowed.
                e.preventDefault();
                void allowAccess();
              }}
            >
              Allow microphone access to see your microphones
            </DropdownPrimitive.Item>
          ) : (
            <>
              <DropdownPrimitive.Item className={item} onSelect={() => void chooseMicrophone(null)}>
                <Check className={`size-4 shrink-0 ${inUse ? 'invisible' : ''}`} aria-hidden />
                <span className="flex-1">Default microphone</span>
              </DropdownPrimitive.Item>
              {mics.map((d, i) => (
                <DropdownPrimitive.Item key={d.deviceId} className={item} onSelect={() => void chooseMicrophone({ deviceId: d.deviceId, label: d.label })}>
                  <Check className={`size-4 shrink-0 ${inUse === d.deviceId ? '' : 'invisible'}`} aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{d.label || `Microphone ${i + 1}`}</span>
                </DropdownPrimitive.Item>
              ))}
              {chosenMissing && <p className="px-2.5 pt-1 pb-1.5 text-xs text-muted">Your chosen microphone ({micLabel.get()}) isn't connected, so the default one is used until it is.</p>}
            </>
          )}
          <DropdownPrimitive.Separator className="my-1 h-px bg-line" />
          <DropdownPrimitive.Item className={item} onSelect={() => ui.open({ kind: 'userSettings', section: 'voice' })}>
            <Settings className="size-4 shrink-0" aria-hidden />
            <span className="flex-1">Voice settings</span>
          </DropdownPrimitive.Item>
        </DropdownPrimitive.Content>
      </DropdownPrimitive.Portal>
    </DropdownPrimitive.Root>
  );
}
